import { useState, useEffect, useCallback, useMemo } from 'react';
import { transporterAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useTransporterVehicles = (filters = {}) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.status,
    filters.vehicle_type,
    filters.make,
    filters.model
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchVehicles = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      const response = await transporterAPI.getVehicles(params);
      setVehicles(response.data.results || response.data);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des véhicules:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des véhicules');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  const createVehicle = useCallback(async (vehicleData) => {
    try {
      const response = await transporterAPI.createVehicle(vehicleData);
      // Recharger la liste après création
      fetchVehicles();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la création du véhicule:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la création du véhicule' 
      };
    }
  }, [fetchVehicles]);

  const updateVehicle = useCallback(async (vehicleId, vehicleData) => {
    try {
      const response = await transporterAPI.updateVehicle(vehicleId, vehicleData);
      // Recharger la liste après modification
      fetchVehicles();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la modification du véhicule:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la modification du véhicule' 
      };
    }
  }, [fetchVehicles]);

  const deleteVehicle = useCallback(async (vehicleId) => {
    try {
      await transporterAPI.deleteVehicle(vehicleId);
      // Recharger la liste après suppression
      fetchVehicles();
      return { success: true };
    } catch (err) {
      console.error('Erreur lors de la suppression du véhicule:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la suppression du véhicule' 
      };
    }
  }, [fetchVehicles]);

  const toggleAvailability = useCallback(async (vehicleId) => {
    try {
      const response = await transporterAPI.toggleVehicleAvailability(vehicleId);
      // Recharger la liste après modification
      fetchVehicles();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors du changement de disponibilité:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors du changement de disponibilité' 
      };
    }
  }, [fetchVehicles]);

  const updateLocation = useCallback(async (vehicleId, locationData) => {
    try {
      const response = await transporterAPI.updateVehicleLocation(vehicleId, locationData);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la localisation:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la mise à jour de la localisation' 
      };
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  return {
    vehicles,
    loading,
    error,
    pagination,
    fetchVehicles,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    toggleAvailability,
    updateLocation,
  };
};
